import {api} from "@/shared/api";
import {FeatureModuleSelectionDialog} from "./featureModuleSelectionDialog";
import {FeatureModuleSelectionDialogConfig} from "./featureModuleSelectionBase";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {isBeforeDay, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {FeatureModule, SaveTrialFeatureModulesResponse} from "@/shared/timr";
import Modal = BootstrapModal.Modal;
const template = `
			<div id="featureModuleSelectionDialog">
				<div id="baseFeatureModules">
				</div>
				<h4 id="extensionFeatureModulesHeader" style="display:none">${messages["pricing.selectionDialog.extensionModules"]}</h4>
				<div id="extensionFeatureModules" class="feature-module-grid-view">
				</div>
			</div>
			`;

/**
 * Custom web component that draws the content of the trial feature module selection dialog.
 */
export class TrialFeatureModuleSelectionDialog extends FeatureModuleSelectionDialog {

	public static show(config: FeatureModuleSelectionDialogConfig): void {
		const dialog = document.createElement("trial-feature-module-selection-dialog") as TrialFeatureModuleSelectionDialog;
		dialog.config = config;
		dialog.show();
	}

	protected connectedCallback() {
		this.innerHTML = template;
		// Disable switch for active modules or expired trial modules
		this.buildFeatureModuleItems(this.config, {
			disableSwitchIf: fm => !!((fm.active && !fm.trialUntil) || (fm.trialUntil && isBeforeDay(parseISODate(fm.trialUntil), Temporal.Now.plainDateISO()))),
			popover: dom => {
				$(dom).popover("destroy").popover({
					trigger: "hover",
					placement: "bottom",
					// Select only disabled and not checked switches
					selector: "input.switchery:disabled:not(:checked) + span.switchery",
					container: ".content-wrapper",
					delay: {show: 0, hide: 0},
					html: true,
					template: `<div class="popover" role="tooltip"><div class="arrow"></div><div class="popover-content p-5"></div></div>`,
					content: messages["pricing.trialSelectionDialog.disabledModuleInfo"]
				});
			}
		});
		this.onFeatureModuleSelected();
	}

	protected show(): void {
		this.modal = new BootstrapModal.Modal({
			title: "",
			message: $(this),
			size: "modal-xl",
			destroyOnClose: true,
			closeButton: false,
			css: {
				content: "pt-10 scrollable-modal"
			},
			buttons: [
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				},
				{
					text: "dialog.close",
					css: "saveButton btn-primary",
					action: (m) => {
						this.saveTrialFeatureModules(m);
					}
				}
			]
		});
		this.modal.show();
	}

	protected buildMonthlyYearlySwitch() {
		const header = this.modal.getDomModal().find(".modal-header");
		header.addClass("pb-10")
			.children()
			.wrapAll(`<div></div>`)
			.parent()
			.wrapAll(`<div class="display-flex justify-content-space-between flex-valign-middle"></div>`)
			.parent()
			.append(`
						<div class="display-flex flex-valign-middle">
							<h4 class="m-0">${messages["pricing.selectionDialog.paymentPeriod"]}</h4>
							<div class="ml-15 btn-group">
								<button id="priceMonthlyButton" class="btn btn-default">${messages["pricing.selectionDialog.paymentPeriod.MONTHLY"]}</button>
								<button id="priceYearlyButton" class="btn btn-primary">${messages["pricing.selectionDialog.paymentPeriod.ANNUALLY"]}</button>
							</div>
						</div>
				`);

		header.find(".modal-title").append(`
				<div class="display-flex flex-valign-middle">
					<h4 class="m-0">${messages.withArguments("pricing.selectionDialog.userCount", this.config.userCount)}</h4>
				</div>
			`);
		header.append(`<div><i class="far fa-info-circle"></i> ${messages["pricing.trialSelectionDialog.header.info"]}</div>`);

		this.initMonthlyYearlySwitch("#priceMonthlyButton", "#priceYearlyButton");
	}

	protected buildModalFooter() {
		const footer = this.modal.getDomModal().find(".modal-footer");
		footer.addClass("p-20 pt-10")
			.children()
			.wrapAll(`<div class="text-nowrap"></div>`)
			.parent()
			.wrapAll(`<div class="display-flex justify-content-space-between"></div>`)
			.parent()
			.prepend(`<div id="totalPrice" class="flex-row ml-0 mt-5"></div>`);
		footer.append(`<div class="text-left" data-info></div>`);
	}

	protected toggleSaveButton(_selectedModules: FeatureModule[]): void {
		const saveButton = document.querySelector(".saveButton");
		const infoElement = document.querySelector("[data-info]");
		const result = this.compareSelectedFeatureModules(this.config.activeFeatureModules, this.getSelectedModules());
		infoElement.textContent = result ? messages["pricing.trialSelectionDialog.price.info"] : "";
		if (result.get("added").length > 0) {
			saveButton.textContent = messages["pricing.trialSelectionDialog.startTrial"];
		} else if (result.get("removed").length > 0) {
			saveButton.textContent = messages["pricing.trialSelectionDialog.endTrial"];
		} else {
			saveButton.textContent = messages["dialog.close"];
		}
	}

	/*
	 * Compares two arrays of feature modules and returns:
	 * - a positive number if new modules were added
	 * - a negative number if modules were removed
	 * - zero if both arrays are equal
	 */
	private compareSelectedFeatureModules(modulesA: FeatureModule[], modulesB: FeatureModule[]): Map<"added" | "removed", FeatureModule[]> {
		const setA = modulesA.map(m => m.id);
		const setB = modulesB.map(m => m.id);

		const added = [...setB].filter(id => !setA.includes(id));
		const removed = [...setA].filter(id => !setB.includes(id));

		return new Map<"added" | "removed", FeatureModule[]>([
			["added", modulesB.filter(m => added.includes(m.id))],
			["removed", modulesA.filter(m => removed.includes(m.id))]
		]);
	}

	private saveTrialFeatureModules(m: Modal) {
		const result = this.compareSelectedFeatureModules(this.config.activeFeatureModules, this.getSelectedModules());
		if (result.get("added").length > 0 || result.get("removed").length > 0) {
			api("pricing/trialForFeatureModules.json")
				.post({
					addedTrialModuleIds: result.get("added").map(fm => fm.id),
					removedTrialModuleIds: result.get("removed").map(fm => fm.id),
				})
				.done((response: SaveTrialFeatureModulesResponse) => {
					m.close(); // close the modal first to avoid double clicks if the buttons get reset
					if (response.reLoginRequired) {
						window.location.href = "/timr/reLoginRequired.html";
					} else {
						window.location.reload();
					}
				})
				.fail(() => m.resetLoadingButtons());
		} else {
			m.close();
		}
	}
}
